import { Fragment } from "react";

import { Menu, Transition } from "@headlessui/react";
import { CheckIcon, LanguageIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

import classNames from "../../../utils/classNames";
import { supportedLanguages } from "../../../i18n";

export default function LanguageSelector() {
  const { t, i18n } = useTranslation("common");

  const currentLanguage =
    supportedLanguages.find((lang) => i18n.language?.startsWith(lang.code)) ?? supportedLanguages[0];

  const handleChangeLanguage = (code: string) => {
    if (code === currentLanguage.code) return;
    i18n.changeLanguage(code).then(() => {
      // Reload so imperative UI state (page titles, document.title, memoised
      // labels) picks up the new language everywhere.
      window.location.reload();
    });
  };

  return (
    <Menu as="div" className="relative">
      <Menu.Button className="inline-flex items-center gap-x-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50">
        <span className="sr-only">{t("Language")}</span>
        <LanguageIcon className="h-5 w-5" aria-hidden="true" />
        <span className="hidden md:inline">{currentLanguage.short}</span>
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 z-10 mt-2 w-40 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          {supportedLanguages.map((lang) => (
            <Menu.Item key={lang.code}>
              {({ active }) => (
                <button
                  type="button"
                  onClick={() => handleChangeLanguage(lang.code)}
                  className={classNames(
                    active ? "bg-gray-100" : "",
                    "flex w-full items-center justify-between px-4 py-2 text-left text-sm text-gray-700"
                  )}
                >
                  {lang.label}
                  {lang.code === currentLanguage.code && (
                    <CheckIcon className="h-4 w-4 text-primary-600" aria-hidden="true" />
                  )}
                </button>
              )}
            </Menu.Item>
          ))}
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
